// src/modules/marketplace/store/realizationApi.ts

import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions"
import {
  RootState,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { baseQueryWithReauth } from "@/modules/app/store"
import { Realization } from "../interfaces"

// ─── Base URL ─────────────────────────────────────────────────────────────
const rawBase = new URL(
  import.meta.env.REACT_APP_ADV_API,
  window.location.origin,
).toString()
const baseUrl = rawBase.endsWith("/") ? rawBase : rawBase + "/"

// ─── Tag types ────────────────────────────────────────────────────────────
const tagTypes = ["Realizations", "RealizationFilters", "Batches"] as const

const reducerPath = "realizationApi"

// ─── Base query with auth ─────────────────────────────────────────────────
const baseQuery: BaseQueryFn = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) headers.set("authorization", `Bearer ${token}`)
    return headers
  },
})

// ─── Helper: serialize query params (supports arrays as repeated keys) ────
function serializeParams(queryParams: Record<string, unknown>): string {
  return Object.entries(queryParams)
    .filter(([, value]) =>
      Array.isArray(value) ? !!value.length : value != null,
    )
    .map(([key, value]) =>
      Array.isArray(value)
        ? value.map((v) => `${key}=${encodeURIComponent(v)}`).join("&")
        : `${key}=${encodeURIComponent(String(value))}`,
    )
    .join("&")
}

type ApiBuilder = EndpointBuilder<
  typeof baseQuery,
  (typeof tagTypes)[number],
  typeof reducerPath
>

const endpoints = (build: ApiBuilder) => ({
  realizationsList: build.query<
    Realization.GetRealizationsRes,
    Realization.GetRealizationsReq
  >({
    query: ({ queryParams }) => ({
      url: `realizations/list`,
      method: "GET",
      params: serializeParams(queryParams as Record<string, unknown>),
    }),
    providesTags: ["Realizations"],
  }),

  realizationsFilters: build.query<
    Realization.GetRealizationFiltersRes,
    Realization.GetRealizationFiltersReq
  >({
    query: () => ({ url: `realizations/filters`, method: "GET" }),
    providesTags: ["RealizationFilters"],
  }),

  batchesList: build.query<
    Realization.GetBatchesRes,
    Realization.GetBatchesReq
  >({
    query: ({ queryParams }) => ({
      url: `batches/list`,
      method: "GET",
      params: serializeParams(queryParams as Record<string, unknown>),
    }),
    providesTags: ["Batches"],
  }),

  createBatch: build.mutation<
    Realization.CreateBatchRes,
    Realization.CreateBatchReq
  >({
    query: ({ body }) => ({ url: `batches`, method: "POST", body }),
    invalidatesTags: ["Batches"],
  }),

  deleteBatch: build.mutation<
    Realization.DeleteBatchRes,
    Realization.DeleteBatchReq
  >({
    query: ({ pathParams: { batch_id } }) => ({
      url: `batches/${batch_id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["Batches"],
  }),

  addCourier: build.mutation<
    Realization.AddCourierRes,
    Realization.AddCourierReq
  >({
    query: ({ pathParams: { batch_id }, body }) => ({
      url: `batches/${batch_id}/couriers`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["Batches"],
  }),

  deleteCourier: build.mutation<
    Realization.DeleteCourierRes,
    Realization.DeleteCourierReq
  >({
    query: ({ pathParams: { batch_id, courier_id } }) => ({
      url: `batches/${batch_id}/couriers/${courier_id}`,
      method: "DELETE",
    }),
    invalidatesTags: ["Batches"],
  }),
})

export const realizationApi = createApi({
  reducerPath,
  tagTypes,
  baseQuery: baseQueryWithReauth(baseQuery),
  endpoints: (build) => ({ ...endpoints(build) }),
})

dynamicMiddlewaresInstance.addMiddleware(realizationApi.middleware)
registerReducer(realizationApi.reducerPath, realizationApi.reducer)

export const {
  useRealizationsListQuery,
  useRealizationsFiltersQuery,
  useBatchesListQuery,
  useCreateBatchMutation,
  useDeleteBatchMutation,
  useAddCourierMutation,
  useDeleteCourierMutation,
} = realizationApi
